import React, { useState } from 'react';
import { copyText } from '../utils/clipboard';
import { trackEvent } from '../utils/analytics';

interface Props {
  text: string;
  title?: string;
  className?: string;
  iconClassName?: string;
  /** Show a "Copied" label next to the check on success (good on mobile). */
  showLabel?: boolean;
  /** Optional content (e.g. the truncated address) — makes the whole thing one
   *  big tap-to-copy target instead of a tiny icon. */
  children?: React.ReactNode;
}

/**
 * Copy-to-clipboard button with a webview-safe copy and unmistakable feedback.
 * Replaces the silent `navigator.clipboard?.writeText(...).catch(()=>{})` that
 * was the #1 rage-click target on the token page (86% mobile) — it failed
 * quietly in in-app browsers and never confirmed success. Emits `copy_ca` with
 * the outcome so we can watch the success rate.
 *
 * Pass `children` (e.g. the address) to turn it into a full-width chip with a
 * comfortable touch target rather than a hard-to-hit 10px icon.
 */
const CopyButton: React.FC<Props> = ({ text, title = 'Copy', className, iconClassName = 'text-[10px]', showLabel = false, children }) => {
  const [copied, setCopied] = useState(false);

  const handle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyText(text);
    trackEvent('copy_ca', { success: ok });
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      title={copied ? 'Copied!' : title}
      aria-label={copied ? 'Copied' : title}
      className={`inline-flex items-center gap-1.5 ${className ?? 'text-crypto-accent hover:text-crypto-accent/80 p-1'}`}
    >
      {children}
      <i className={`fas ${copied ? 'fa-check' : 'fa-copy'} ${iconClassName}`} />
      {(showLabel || children) && copied && <span className="text-[11px] font-medium text-crypto-accent">Copied</span>}
    </button>
  );
};

export default CopyButton;
